import { useMemo, useRef, useState, type ReactNode } from 'react';
import { Asterisk, Check, CheckCircle2, ChevronLeft, ChevronRight, LayoutList, Search } from 'lucide-react';
import { ProjectMark } from '../project/ProjectMark';
import { ScopeBar } from '../scope/ScopeBar';
import { Card } from '../ui/card';
import { Select } from '../ui/input';
import type { KbChange, KbReviewStatus, ProjectScopeLevel } from '../../lib/api';
import type { ScopeState } from '../../lib/scope';
import { cn } from '../../lib/utils';
import { groupReviewItems, REVIEW_TYPE_META, REVIEW_TYPES, reviewTypeOf, type ReviewItem, type ReviewType } from './review-model';
import { ReviewCard, ReviewGroupCard } from './ReviewCard';

type TypeCounts = Record<ReviewType, number>;

function emptyCounts(): TypeCounts {
  return { conflict: 0, personal: 0, unsure: 0, fix: 0 };
}

function TypeDots({ counts }: { counts: TypeCounts }) {
  return (
    <span className="flex items-center gap-1.5 text-[10px] text-ink-muted tabular-nums">
      {REVIEW_TYPES.filter((t) => counts[t] > 0).map((t) => (
        <span key={t} className="flex items-center gap-1" title={REVIEW_TYPE_META[t].label}>
          <span className={cn('w-1.5 h-1.5 rounded-full', REVIEW_TYPE_META[t].dot)} />
          {counts[t]}
        </span>
      ))}
    </span>
  );
}

const STATUS_OPTIONS: Array<{ label: string; value: KbReviewStatus | 'ALL' }> = [
  { label: 'Waiting for you', value: 'PENDING' },
  { label: 'Kept', value: 'ACCEPTED' },
  { label: 'Undone', value: 'REJECTED' },
  { label: 'Applied without review', value: 'NOT_REQUIRED' },
  { label: 'Everything', value: 'ALL' },
];

const SUMMARY_ORDER: ReviewType[] = ['conflict', 'personal', 'unsure', 'fix'];
const SUMMARY_WORD: Record<ReviewType, [string, string]> = {
  conflict: ['conflict', 'conflicts'],
  personal: ['sensitive', 'sensitive'],
  unsure: ['unsure', 'unsure'],
  fix: ['from a fix', 'from fixes'],
};

/**
 * The review queue, laid out around the question a reviewer opens it with — "what needs me, and
 * where" — rather than around the rows. Every decision still goes through the classic card's calls;
 * this component only arranges them: a progress line, a tile per project with work waiting, and the
 * cards grouped under the project they belong to. Sizes, radii and tones are the console's own
 * (`Card`, `Badge`, the classic card's kind labels), so the page reads as part of the product.
 */
export function ReviewQueue({
  changes, levels, pendingCount, status, onStatus, levelFilter, onPickProject, mineOnly, onMineOnly,
  scope, projectId, loading, error, toast, onResolved, onOpenNode, onClassic,
}: {
  changes: KbChange[];
  levels: ProjectScopeLevel[];
  pendingCount: number | null;
  status: KbReviewStatus | 'ALL';
  onStatus: (status: KbReviewStatus | 'ALL') => void;
  /** The project the queue is narrowed to, if any — one tile or one All projects row. */
  levelFilter: string[];
  onPickProject: (projectId: string | null) => void;
  mineOnly: boolean;
  onMineOnly: (mine: boolean) => void;
  scope: ScopeState;
  projectId: string;
  loading: boolean;
  error: string | null;
  toast: string | null;
  onResolved: (message: string, changedProjectId: string) => void;
  onOpenNode: (path: string, changedProjectId: string) => void;
  onClassic: () => void;
}) {
  const [browsing, setBrowsing] = useState(false);
  const lensLevels = levels.filter((l) => !mineOnly || l.mine);
  const busyLevels = lensLevels.filter((l) => l.pendingChanges > 0);
  const quietCount = lensLevels.length - busyLevels.length;
  const selectedId = levelFilter.length === 1 ? levelFilter[0]! : null;
  // A project picked from All projects with nothing waiting still gets a tile, so the strip always
  // shows what the queue below is narrowed to.
  const selectedQuiet = selectedId && !busyLevels.some((l) => l.id === selectedId)
    ? levels.find((l) => l.id === selectedId) ?? null
    : null;
  const outsidePending = levels.filter((l) => !l.mine).reduce((n, l) => n + l.pendingChanges, 0);
  const lensTotal = busyLevels.reduce((n, l) => n + l.pendingChanges, 0);
  const waiting = levels.length > 0 ? lensTotal : pendingCount ?? 0;

  const pendingChanges = changes.filter((c) => c.reviewStatus === 'PENDING');
  const byReason = useMemo(() => {
    const counts = new Map<ReviewType, number>();
    for (const c of pendingChanges) {
      const type = reviewTypeOf(c);
      if (type) counts.set(type, (counts.get(type) ?? 0) + 1);
    }
    return SUMMARY_ORDER.filter((t) => counts.has(t)).map((t) => ({ type: t, count: counts.get(t)! }));
  }, [pendingChanges]);

  /** Cards bucketed by project, in the hierarchy's own order so the page reads like the sidebar. */
  const buckets = useMemo(() => {
    const order = new Map(levels.map((l, i) => [l.id, i]));
    const byProject = new Map<string, ReviewItem[]>();
    for (const item of groupReviewItems(changes)) {
      byProject.set(item.projectId, [...(byProject.get(item.projectId) ?? []), item]);
    }
    return [...byProject.entries()].sort(
      ([a], [b]) => (order.get(a) ?? Number.MAX_SAFE_INTEGER) - (order.get(b) ?? Number.MAX_SAFE_INTEGER),
    );
  }, [changes, levels]);

  /**
   * Waiting changes per project and reason, for the dots on tiles and rows. The server counts only
   * totals per project, so these come from the loaded rows — and a narrowed load holds one project's
   * rows only, so the last full reading is kept and just the narrowed project's entry refreshed.
   */
  const lastCounts = useRef(new Map<string, TypeCounts>());
  const typeCounts = useMemo(() => {
    if (status !== 'PENDING') return lastCounts.current;
    const fresh = new Map<string, TypeCounts>();
    for (const c of pendingChanges) {
      const type = reviewTypeOf(c);
      if (!type) continue;
      const counts = fresh.get(c.projectId) ?? emptyCounts();
      counts[type]++;
      fresh.set(c.projectId, counts);
    }
    const next = levelFilter.length === 0 ? fresh : new Map(lastCounts.current);
    if (levelFilter.length > 0) for (const id of levelFilter) next.set(id, fresh.get(id) ?? emptyCounts());
    lastCounts.current = next;
    return next;
  }, [pendingChanges, status, levelFilter]);

  /** Dots only when they add up to the project's own total — a capped page can hold only some. */
  const dotsFor = (level: ProjectScopeLevel): TypeCounts | null => {
    const counts = typeCounts.get(level.id);
    if (!counts) return null;
    return REVIEW_TYPES.reduce((n, t) => n + counts[t], 0) === level.pendingChanges ? counts : null;
  };
  const allDots = useMemo(() => {
    const total = emptyCounts();
    for (const level of busyLevels) {
      const counts = dotsFor(level);
      if (!counts) return null;
      for (const t of REVIEW_TYPES) total[t] += counts[t];
    }
    return total;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busyLevels, typeCounts]);

  const nameFor = (id: string) => scope.projectFor(id)?.name ?? levels.find((l) => l.id === id)?.name ?? null;

  if (browsing) {
    return (
      <AllProjects
        levels={levels}
        scope={scope}
        dotsFor={dotsFor}
        onBack={() => setBrowsing(false)}
        onPick={(id) => {
          setBrowsing(false);
          onPickProject(id);
        }}
      />
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-7 py-7">
      <div className="mb-4">
        <h1 className="text-lg font-bold text-ink flex items-center gap-2">
          Review
          {waiting > 0 && (
            <span className="text-[11px] font-semibold text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-950/50 rounded-full px-2 py-0.5">
              {waiting}
            </span>
          )}
        </h1>
        <p className="text-xs text-ink-muted mt-1 flex items-center gap-3 flex-wrap">
          {status === 'PENDING' && byReason.length > 0
            ? byReason.map(({ type, count }) => (
                <span key={type} className="inline-flex items-center gap-1.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${REVIEW_TYPE_META[type].dot}`} />
                  {count} {SUMMARY_WORD[type][count === 1 ? 0 : 1]}
                </span>
              ))
            : waiting === 0
              ? 'Nothing needs you right now.'
              : `${waiting} waiting.`}
        </p>
      </div>

      {/* One toolbar: the two filters side by side at the same size, the secondary links on the right. */}
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        <Select
          value={status}
          onChange={(e) => onStatus(e.target.value as KbReviewStatus | 'ALL')}
          className="w-auto py-1.5 text-xs"
          aria-label="Which changes to show"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </Select>
        <ScopeBar scope={scope} />
        <div className="ml-auto flex items-center gap-4 text-[11px]">
          {mineOnly && outsidePending > 0 ? (
            <button onClick={() => onMineOnly(false)} className="font-medium text-ink-muted hover:text-ink">
              Include other projects ({outsidePending} more)
            </button>
          ) : !mineOnly && levels.some((l) => l.mine) && levels.some((l) => !l.mine) ? (
            <button onClick={() => onMineOnly(true)} className="font-medium text-ink-muted hover:text-ink">
              Only my projects
            </button>
          ) : null}
          <button
            onClick={onClassic}
            className="inline-flex items-center gap-1 text-ink-faint hover:text-ink transition-colors"
            title="Switch to the detailed review layout"
          >
            <LayoutList size={11} />Detailed layout
          </button>
        </div>
      </div>

      {/* A tile per project with work waiting; one click narrows the queue below to it. */}
      {levels.length > 0 && (
        <div className="mb-5">
          <div className="flex gap-2 overflow-x-auto pb-1">
            <Tile
              selected={selectedId === null}
              onClick={() => onPickProject(null)}
              mark={
                <span className="w-4 h-4 rounded-[4px] bg-orange-500 text-white flex items-center justify-center flex-shrink-0">
                  <Asterisk size={10} strokeWidth={3} />
                </span>
              }
              name={mineOnly ? 'My projects' : 'All projects'}
              count={waiting}
              counts={allDots}
            />
            {[...busyLevels, ...(selectedQuiet ? [selectedQuiet] : [])].map((level) => {
              const project = scope.projectFor(level.id);
              return (
                <Tile
                  key={level.id}
                  selected={selectedId === level.id}
                  onClick={() => onPickProject(selectedId === level.id ? null : level.id)}
                  mark={project ? <ProjectMark project={project} /> : null}
                  name={level.name}
                  count={level.pendingChanges}
                  counts={dotsFor(level)}
                />
              );
            })}
          </div>
          <div className="mt-2 flex items-center justify-between gap-3 text-[11px]">
            {quietCount > 0 ? (
              <span className="flex items-center gap-1.5 text-ink-faint">
                <CheckCircle2 size={12} className="text-green-600 dark:text-green-400" />
                {quietCount} other {quietCount === 1 ? 'project has' : 'projects have'} nothing to review
              </span>
            ) : (
              <span />
            )}
            <button onClick={() => setBrowsing(true)} className="flex items-center gap-0.5 font-medium text-orange-500 hover:text-orange-700">
              Show all projects <ChevronRight size={12} />
            </button>
          </div>
        </div>
      )}

      {toast && (
        <p className="mb-4 text-xs text-ink-secondary bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900 rounded px-3 py-2">
          {toast}
        </p>
      )}
      {error && (
        <p className="mb-4 text-xs text-danger-text bg-danger-bg border border-danger-ring rounded px-3 py-2">{error}</p>
      )}

      {loading ? (
        <p className="text-xs text-ink-faint py-8 text-center">Loading…</p>
      ) : changes.length === 0 ? (
        <Card className="px-6 py-14 text-center">
          <Check size={22} className="text-ink-faint mx-auto mb-3" />
          <p className="text-sm text-ink-secondary mb-1">
            {status === 'PENDING' ? "You're all caught up" : 'No changes here'}
          </p>
          <p className="text-xs text-ink-faint max-w-sm mx-auto">
            {status === 'PENDING'
              ? 'Only conflicting information, sensitive information, and edits the agent was unsure of come here.'
              : 'Try another filter.'}
          </p>
          {mineOnly && outsidePending > 0 && (
            <p className="text-xs text-ink-faint mt-2">
              {outsidePending} waiting in projects outside yours —{' '}
              <button onClick={() => onMineOnly(false)} className="text-orange-500 hover:text-orange-600 underline">
                include them
              </button>
            </p>
          )}
        </Card>
      ) : (
        <div className="space-y-6">
          {buckets.map(([bucketProjectId, items]) => {
            const project = scope.projectFor(bucketProjectId);
            const parent = project?.parentId ? scope.projectFor(project.parentId) : null;
            const waitingHere = items.reduce(
              (n, item) => n + item.changes.filter((c) => c.reviewStatus === 'PENDING').length,
              0,
            );
            return (
              <section key={bucketProjectId} className="space-y-2.5">
                <div className="flex items-center gap-2">
                  {project && <ProjectMark project={project} />}
                  <h2 className="text-sm font-semibold text-ink">{nameFor(bucketProjectId) ?? 'Unknown project'}</h2>
                  {parent && parent.parentId !== null && <span className="text-[11px] text-ink-faint">in {parent.name}</span>}
                  {waitingHere > 0 && (
                    <span className="ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-400">
                      {waitingHere} to review
                    </span>
                  )}
                </div>
                {items.map((item) =>
                  item.changes.length > 1 ? (
                    <ReviewGroupCard
                      key={item.key}
                      item={item}
                      projectId={projectId}
                      projectNameFor={nameFor}
                      onResolved={onResolved}
                      onOpenNode={onOpenNode}
                    />
                  ) : (
                    <ReviewCard
                      key={item.key}
                      change={item.changes[0]!}
                      projectId={projectId}
                      onResolved={onResolved}
                      onOpenNode={onOpenNode}
                    />
                  ),
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Tile({
  selected, onClick, mark, name, count, counts,
}: {
  selected: boolean;
  onClick: () => void;
  mark: ReactNode;
  name: string;
  count: number;
  counts: TypeCounts | null;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-40 flex-shrink-0 rounded-lg border bg-card px-3 py-2.5 text-left transition-colors',
        selected ? 'border-orange-500 ring-1 ring-orange-500' : 'border-border hover:border-orange-300',
      )}
    >
      <span className="flex items-center gap-1.5 min-w-0">
        {mark}
        <span className="text-xs font-medium text-ink truncate">{name}</span>
      </span>
      <span className="mt-2 flex items-end justify-between gap-2">
        <span className="text-xl leading-none font-semibold text-ink tabular-nums">{count}</span>
        {counts && <TypeDots counts={counts} />}
      </span>
    </button>
  );
}

/**
 * Every project in scope, grouped under its parent, with what waits in each — what "Show all
 * projects" opens. Picking one returns to the queue narrowed to it. Lists the whole scope rather than
 * the My projects lens: a project outside it is exactly what someone comes here to find.
 */
function AllProjects({
  levels, scope, dotsFor, onBack, onPick,
}: {
  levels: ProjectScopeLevel[];
  scope: ScopeState;
  dotsFor: (level: ProjectScopeLevel) => TypeCounts | null;
  onBack: () => void;
  onPick: (projectId: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [show, setShow] = useState<'needs' | 'all'>('all');
  const needCount = levels.filter((l) => l.pendingChanges > 0).length;

  // Grouped by parent, in the hierarchy's order. The organization root heads the group of its own
  // children, so the top level reads as one group rather than a lone row.
  const groups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const out = new Map<string, ProjectScopeLevel[]>();
    for (const level of levels) {
      if (show === 'needs' && level.pendingChanges === 0) continue;
      if (needle && !level.name.toLowerCase().includes(needle)) continue;
      const key = scope.projectFor(level.id)?.parentId ?? level.id;
      out.set(key, [...(out.get(key) ?? []), level]);
    }
    return [...out.entries()];
  }, [levels, query, show, scope]);

  return (
    <div className="mx-auto max-w-3xl px-7 py-7">
      <button onClick={onBack} className="mb-4 flex items-center gap-0.5 text-xs font-medium text-orange-500 hover:text-orange-700">
        <ChevronLeft size={13} />Review
      </button>
      <h1 className="text-lg font-bold text-ink">All projects</h1>
      <p className="text-xs text-ink-muted mt-1">
        {levels.length} project{levels.length === 1 ? '' : 's'} · {needCount} need{needCount === 1 ? 's' : ''} you
      </p>

      <div className="flex items-center gap-2 mt-4 mb-5">
        <div className="relative flex-1">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search projects"
            className="w-full pl-7 pr-3 py-1.5 text-xs bg-card border border-border rounded-md outline-none focus:border-orange-400 placeholder:text-ink-faint text-ink"
          />
        </div>
        <div className="flex border border-border rounded-md overflow-hidden text-xs flex-shrink-0">
          {([['needs', `Needs you · ${needCount}`], ['all', `All · ${levels.length}`]] as const).map(([value, label]) => (
            <button
              key={value}
              onClick={() => setShow(value)}
              className={cn(
                'px-2.5 py-1.5 transition-colors',
                show === value ? 'bg-surface-muted text-ink font-medium' : 'bg-card text-ink-muted hover:text-ink',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {groups.length === 0 ? (
        <p className="text-xs text-ink-faint py-8 text-center">
          {show === 'needs' && !query ? 'Nothing needs you in any project.' : 'No matching projects'}
        </p>
      ) : (
        <div className="space-y-5">
          {groups.map(([groupId, rows]) => (
            <section key={groupId}>
              <div className="flex items-center justify-between px-1 mb-1.5 text-[11px] font-semibold text-ink-faint">
                <span>{scope.projectFor(groupId)?.name ?? levels.find((l) => l.id === groupId)?.name ?? 'Projects'}</span>
                <span className="tabular-nums">{rows.length}</span>
              </div>
              <Card className="divide-y divide-border-subtle overflow-hidden">
                {rows.map((level) => {
                  const project = scope.projectFor(level.id);
                  const parent = project?.parentId ? scope.projectFor(project.parentId) : null;
                  const dots = level.pendingChanges > 0 ? dotsFor(level) : null;
                  return (
                    <button
                      key={level.id}
                      onClick={() => onPick(level.id)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-surface-subtle transition-colors"
                    >
                      {project && <ProjectMark project={project} />}
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-ink truncate">{level.name}</span>
                        <span className="block text-[11px] text-ink-faint truncate">
                          {parent ? `in ${parent.name}` : 'Top level'}
                        </span>
                      </span>
                      {level.pendingChanges > 0 ? (
                        <span className="flex items-center gap-2 flex-shrink-0">
                          {dots && <TypeDots counts={dots} />}
                          <span className="text-[11px] font-semibold text-orange-600 dark:text-orange-400 bg-orange-100 dark:bg-orange-950/50 rounded-full px-2 py-0.5 tabular-nums">
                            {level.pendingChanges}
                          </span>
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-[11px] text-ink-faint flex-shrink-0">
                          <CheckCircle2 size={12} className="text-green-600 dark:text-green-400" />All clear
                        </span>
                      )}
                      <ChevronRight size={13} className="text-ink-faint flex-shrink-0" />
                    </button>
                  );
                })}
              </Card>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
